import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Download, Loader2, X } from "lucide-react";
import { getPaymentStatus, quotePdfUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LangSwitcher } from "@/components/Nav";

export default function PaymentResult({ type }) {
  const { lang, t } = useLang();
  const [state, setState] = useState(type === "success" ? "pending" : "cancelled");
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (type !== "success") return;
    if (!sessionId) {
      setState("pending_review");
      return;
    }
    let tries = 0;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) {
        /* transient */
      }
      if (++tries < 8 && !stopped) setTimeout(tick, 2500);
      else setState("pending_review");
    };
    tick();
    return () => {
      stopped = true;
    };
  }, [type, sessionId]);

  const success = state === "paid";
  const cancelled = type === "cancel";

  const title = cancelled ? (
    <>{t.payment.cancelledTitleA}<em className="font-serif-accent text-[#C5B358]">{t.payment.cancelledEm}</em></>
  ) : success ? (
    <>{t.payment.paidTitleA}<em className="font-serif-accent text-[#C5B358]">{t.payment.paidEm}</em></>
  ) : (
    <>{t.payment.pendingTitleA}<em className="font-serif-accent text-[#C5B358]">{t.payment.pendingEm}</em></>
  );

  const body = cancelled
    ? t.payment.cancelledBody
    : success
    ? t.payment.paidBody
    : t.payment.pendingBody;

  return (
    <div className="min-h-screen bg-[#0F1E3D] grid-pattern noise flex items-center justify-center px-6">
      <div className="fixed top-5 right-5 z-50">
        <LangSwitcher />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="text-center max-w-md"
        data-testid="payment-result-page"
      >
        <div
          className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
            cancelled ? "bg-white/10" : success ? "bg-[#C5B358]/20" : "bg-white/10"
          }`}
          data-testid="payment-status-icon"
        >
          {cancelled ? (
            <X size={30} className="text-[#A3B1C6]" />
          ) : success ? (
            <Check size={30} strokeWidth={2.5} className="text-[#C5B358]" />
          ) : (
            <Loader2 size={26} className="text-[#C5B358] animate-spin" />
          )}
        </div>

        <h1 className="font-display mt-8 text-4xl font-bold text-white">{title}</h1>

        <p className="mt-5 text-[#A3B1C6] leading-relaxed" data-testid="payment-status-text">
          {body}
        </p>

        <div className="mt-10 flex flex-col items-center gap-4">
          {cancelled ? (
            <a
              href="/"
              data-testid="payment-back-btn"
              className="inline-flex items-center gap-3 border border-[#C5B358]/60 text-[#C5B358] hover:bg-[#C5B358] hover:text-[#0F1E3D] transition-all duration-300 rounded-full px-8 py-4 text-[11px] font-bold uppercase tracking-[0.12em]"
            >
              <ArrowLeft size={14} /> {t.payment.back}
            </a>
          ) : (
            <>
              {sessionId && (
                <a
                  href={quotePdfUrl(sessionId, lang)}
                  data-testid="payment-pdf-btn"
                  className="inline-flex items-center gap-3 bg-[#C5B358] hover:bg-[#B3A146] text-[#0F1E3D] transition-all duration-300 rounded-full px-8 py-4 text-[11px] font-bold uppercase tracking-[0.12em]"
                >
                  <Download size={14} /> {t.payment.pdf}
                </a>
              )}
              <a
                href="/"
                data-testid="payment-back-btn"
                className="inline-flex items-center gap-3 border border-[#C5B358]/60 text-[#C5B358] hover:bg-[#C5B358] hover:text-[#0F1E3D] transition-all duration-300 rounded-full px-8 py-4 text-[11px] font-bold uppercase tracking-[0.12em]"
              >
                <ArrowLeft size={14} /> {t.payment.back}
              </a>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}